import { Send } from "@mui/icons-material";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { WarehouseMessage } from "../../types";
import { useTranslation } from "../../../../hooks/useTranslation";

type Props = {
  messages: WarehouseMessage[];
  onSendMessage: (_message: string) => Promise<void>;
  isSending?: boolean;
};

const MAX_MESSAGE_LENGTH = 2000;

export const WarehouseMessages = ({
  messages,
  onSendMessage,
  isSending = false,
}: Props) => {
  const { translate } = useTranslation("pages.orderDetails");
  const [draft, setDraft] = useState("");
  const messageListRef = useRef<HTMLDivElement | null>(null);

  const sortedMessages = useMemo(
    () => [...messages].sort((a, b) => a.created.localeCompare(b.created)),
    [messages],
  );

  useEffect(() => {
    if (!messageListRef.current) {
      return;
    }

    messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
  }, [sortedMessages.length]);

  const trimmedDraft = draft.trim();
  const isTooLong = trimmedDraft.length > MAX_MESSAGE_LENGTH;
  const isInvalid = isTooLong;
  const canSend =
    !isSending &&
    trimmedDraft.length > 0 &&
    trimmedDraft.length <= MAX_MESSAGE_LENGTH;

  const handleSend = async () => {
    if (!canSend) {
      return;
    }

    try {
      await onSendMessage(trimmedDraft);
      setDraft("");
    } catch {
      // Error notification is handled by mutation layer.
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: (theme) => theme.palette.customBackground.box,
        borderLeft: "3px solid",
        borderColor: "info.main",
      }}
    >
      <Typography variant="subtitle1" fontWeight={500}>
        {translate("warehouseMessages.title")}
      </Typography>

      <Divider sx={{ my: 1 }} />

      {sortedMessages.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {translate("warehouseMessages.empty")}
        </Typography>
      ) : (
        <Stack
          ref={messageListRef}
          spacing={1.5}
          sx={{
            maxHeight: 360,
            overflowY: "auto",
            pr: 0.5,
            py: 1,
          }}
        >
          {sortedMessages.map((message) => {
            const isSyncrosale = message.direction === "SYNCROSALE";
            const isCritical = message.message === "Don't send to customer";
            const borderColor = isCritical
              ? "error.main"
              : isSyncrosale
                ? "primary.main"
                : "divider";

            return (
              <Box
                key={message.id}
                sx={{
                  alignSelf: isSyncrosale ? "flex-start" : "flex-end",
                  maxWidth: { xs: "100%", sm: "82%" },
                  p: 1.25,
                  borderRadius: 2,
                  borderTopLeftRadius: isSyncrosale ? 0 : 16,
                  borderTopRightRadius: isSyncrosale ? 16 : 0,
                  backgroundColor: "transparent",
                  color: "text.primary",
                  border: isCritical ? "2px solid" : "1px solid",
                  borderColor,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                  <Chip
                    size="small"
                    color={isSyncrosale ? "primary" : "default"}
                    label={translate(`warehouseMessages.${message.author}`, {
                      defaultValue: message.author,
                    })}
                    sx={{ height: 20 }}
                  />

                  <Typography variant="caption" color="text.secondary">
                    {new Date(message.created).toLocaleString()}
                  </Typography>
                </Stack>

                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                >
                  {message.message}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}

      <Divider sx={{ my: 1.5 }} />

      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          maxRows={8}
          placeholder={translate("warehouseMessages.placeholder", {
            defaultValue: "Type a message...",
          })}
          value={draft}
          disabled={isSending}
          error={isInvalid}
          helperText={
            isTooLong
              ? translate("warehouseMessages.maxLengthError", {
                  max: MAX_MESSAGE_LENGTH,
                })
              : `${trimmedDraft.length}/${MAX_MESSAGE_LENGTH}`
          }
          inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              void handleSend();
            }
          }}
        />

        <IconButton
          color="primary"
          onClick={() => void handleSend()}
          disabled={!canSend}
        >
          <Send fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
};
